/**
 * Transform Listing from API to Listing used in the app.
 *
 * This takes a "listing" from the API and returns a "Listing" item, which is shaped
 * a bit more towards how the app is using the information. It mostly equals the API listing "details"
 * but e.g. media is transformed to suit the app needs.
 */
export function transformListing(apiListing: ApiSchema['Listing'] | undefined): Listing {
    const listing = {
        id: apiListing?.id,
        title: apiListing?.title ?? '',
        lead: apiListing?.details?.importantInfo ?? '',
        description: apiListing?.details?.description,
        startingPrice: apiListing?.details?.startingPrice,
        included: apiListing?.details?.included,
        highlights: apiListing?.details?.highlights,
        startingPoint: apiListing?.details?.startingPoint,
        media: {
            teaser: undefined,
            images: [],
            videos: [],
            map: {
                small: undefined,
                large: undefined,
            },
        } as Listing['media'],
    } as Listing;

    // Listing is determined to be an Activity if it has activityParts
    if (apiListing?.activityParts?.length) {
        const listingActivity = listing as ListingActivity;
        listingActivity.type = 'Activity';
        listingActivity.supplier = apiListing?.activityParts?.[0]?.supplier?.name ?? '';
        listingActivity.activityId = apiListing?.activityParts?.[0]?.activityId;
    }
    // Otherwise, it is a P2P listing, if it includes ":trips:" in its id
    else if (apiListing?.id?.includes(':trips:')) {
        const listingP2P = listing as ListingP2P;
        listingP2P.type = 'P2P';
        const destinationImage = apiListing?.details?.media?.images?.find(image => image.tags?.includes('DESTINATION'));
        const destinationVideo = apiListing?.details?.media?.videos?.find(video => video.tags?.includes('DESTINATION'));

        listingP2P.media.journeyDestination = {
            label: apiListing?.title,
            image: destinationImage?.url,
            video: destinationVideo?.url,
        };
    }

    const teaserImage = apiListing?.details?.media?.images?.find(image => image.tags?.includes('TEASER'));
    if (teaserImage?.url) {
        listing.media.teaser = {
            url: teaserImage.url,
        };
    }

    // Map images are determined by the "MAP" tag. We flatten both the images and its "variants", which may container
    // alternative images to be used for small/medium map images
    const mapImages = [
        ...(apiListing?.details?.media?.images?.filter(image => image.tags?.includes('MAP')) ?? []),
        ...apiListing?.details?.media?.images?.filter(image => image.tags?.includes('MAP'))?.flatMap(image => image.variants) ?? [],
    ];

    // Deprecated images have no tags, so exclude maps and their variants by URL.
    listing.media.images = [
        ...(apiListing?.details?.media?.images?.filter(image => !image.tags?.length) ?? []),
        ...(apiListing?.details?.images?.filter(image => !mapImages.some(mapImage => mapImage?.url === image.url)) ?? []),
    ].map(image => ({
        url: image.url,
    }));

    // Consider the smallest map image to be the "small" map image, and the largest to be the "large" map image
    // Quite a volatile solution, but that's it for now.
    const mapImageSmall = mapImages.sort((a, b) => (a?.width ?? Number.MAX_VALUE) - (b?.width ?? Number.MAX_VALUE))?.[0];
    if (mapImageSmall) {
        listing.media.map.small = {
            url: mapImageSmall?.url,
        };
    }

    // Consider the largest map image to be the "large" map image.
    const mapImageLarge = mapImages.sort((a, b) => (b?.width ?? 0) - (a?.width ?? 0))?.[0];
    if (mapImageLarge) {
        listing.media.map.large = {
            url: mapImageLarge?.url,
        };
    }

    return listing;
}
